import { useQuery } from '@tanstack/react-query';
import { CustomPageType } from '../../extViewmodels/sdk/models/CustomPageStyle';
import { useApiClientContext } from '../contexts/apiClient';

const BASE_QUERY_KEY = 'ScheduleMeetingCustomPageStyle';

interface CustomPageStyleResponse {
	html?: string;
	hasCustomization: boolean;
	error?: string;
}

/**
 * Fetches custom page styles for a meeting by shortCode with fallback chain support.
 *
 * Fallback Chain (implemented by backend):
 * 1. Meeting-specific customization (if brandingOverride exists in meeting config)
 * 2. Account-level customization
 * 3. System default (no customization)
 *
 * Backend API Requirements:
 * - Endpoint: GET /scheduler/{shortCode}/custom-page-style/{type}
 * - Backend must handle fallback logic automatically
 * - Should check meeting config's brandingOverride first
 * - Falls back to account-level if override not present or useAccountDefault is true
 * - Returns 404 if no customization exists at any level
 * - Returns HTML/CSS content if customization found
 *
 * Query Key Structure:
 * - [BASE_QUERY_KEY, shortCode, type]
 * - Each meeting shortCode gets its own cache entry
 * - Type differentiation allows separate caching for Header vs Footer
 *
 * @param shortCode - Meeting short code for identifying the specific meeting configuration
 * @param type - Type of customization (Header, Footer, etc.)
 * @param enabled - Whether the query should execute
 */
export const useCustomPageStyleByShortCode = ({
	shortCode,
	type,
	enabled = true,
}: {
	shortCode?: string;
	type: CustomPageType;
	enabled?: boolean;
}) => {
	const { apiClient } = useApiClientContext();

	return useQuery<CustomPageStyleResponse>({
		enabled: enabled && !!shortCode,
		queryFn: async (): Promise<CustomPageStyleResponse> => {
			try {
				// Backend handles fallback chain:
				// 1. Check meeting-specific override (from meeting config's brandingOverride)
				// 2. Fall back to account-level customization
				// 3. Fall back to no customization
				const response = await fetch(`${apiClient.baseUrl}/scheduler/${shortCode}/custom-page-style/${type}`);

				const htmlContent = await response.text();

				if (!htmlContent || htmlContent.trim() === '') {
					return { hasCustomization: false, error: 'Empty response received' };
				}

				return {
					hasCustomization: true,
					html: htmlContent,
				};
			} catch (networkError) {
				console.error('Network error fetching custom page style:', networkError);
				return { hasCustomization: false, error: 'Network error occurred' };
			}
		},
		// Query key includes shortCode and type to ensure proper caching per meeting and style type
		queryKey: [BASE_QUERY_KEY, shortCode, type],
		retry: (failureCount, error) => {
			// Only retry on network errors, not on 4xx client errors
			return failureCount < 2 && !(error as any)?.message?.includes('HTTP 4');
		},
	});
};
